import moment from 'moment';

import { Box, Flex, Text, UserGroupBadge } from '@frontend/components';
import { useDateFormat } from '@frontend/utils';

import OrderStatusBadge from 'components/lib2/OrderStatusBadge';

import { useAppSelector } from 'store/hooks';
import { getActiveGroup } from 'store/session/selectors';

import { useUserGroupBadgeLookup } from 'utils/multisite';
import { useUserGroupCollectionContext } from 'utils/multisite/UserGroupCollectionProvider';
import { toReadableString } from 'utils/stringUtils';

import HighlightOrDisplay from './HighlightOrDisplay';
import LabelText from './LabelText';
import ValueText from './ValueText';

const PaddedCell = ({ children }: { children?: React.ReactNode }) => (
  <td style={{ paddingRight: '8px' }}>{children}</td>
);

const OrderMatchCell = ({
  orderResult,
}: {
  orderResult: { data: Record<string, any>; highlight: any };
}) => {
  const { dateAtTimeFormat } = useDateFormat();
  const getBadgeProps = useUserGroupBadgeLookup();
  const badgeProps = getBadgeProps(orderResult.data.ownerId);

  const href = `/orders/edit/${orderResult.data.number}`;

  const activeGroup = useAppSelector(getActiveGroup);
  const isFromOutsideGroup = orderResult.data.ownerId !== activeGroup.id;
  const { initChangeSite } = useUserGroupCollectionContext();

  const linkProps = isFromOutsideGroup
    ? {
        onClick: () =>
          initChangeSite({
            nextUrl: href,
            userGroupId: orderResult.data.ownerId,
          }),
      }
    : { href };

  return (
    <Box
      as="a"
      display="block"
      width="100%"
      height="100%"
      transition="0.2s ease"
      _hover={{
        bg: 'interaction.surfaceHover',
      }}
      _focus={{
        boxShadow: 'focusInset',
      }}
      _active={{
        bg: 'interaction.surfaceActive',
      }}
      padding={1}
      {...linkProps}
    >
      <Flex
        justifyContent="space-between"
        alignItems="center"
        paddingBottom={0.5}
        minWidth={0}
        width="100%"
      >
        <Flex alignItems="center" flexShrink={0}>
          <Text
            fontFamily="data"
            fontSize="sm"
            fontWeight="heavy"
            color="feature.primary"
          >
            {`Order #`}
            <HighlightOrDisplay
              result={orderResult}
              field="number"
              textProps={{ fontWeight: 'heavy' }}
              useDataFont
            />
          </Text>
          <OrderStatusBadge
            flexShrink={0}
            marginLeft={0.5}
            size="sm"
            orderStatus={orderResult.data.status}
          />
        </Flex>
        {badgeProps && (
          <Box
            flex={1}
            minWidth={0}
            marginLeft={1}
            display="flex"
            justifyContent="flex-end"
            overflow="hidden"
          >
            <UserGroupBadge
              maxWidth="100%"
              size="sm"
              name={badgeProps.name}
              colorPalette={badgeProps.colorPalette}
              icon={badgeProps.icon}
            />
          </Box>
        )}
      </Flex>
      <table style={{ borderCollapse: 'collapse' }}>
        <tbody>
          <tr>
            <PaddedCell>
              <LabelText>CONTACT</LabelText>
            </PaddedCell>
            <td>
              <HighlightOrDisplay result={orderResult} field="accountName" />
            </td>
          </tr>
          <tr>
            <PaddedCell />
            <td>
              <HighlightOrDisplay
                result={orderResult}
                field="contactFullName"
              />
            </td>
          </tr>
          <tr>
            <PaddedCell />
            <td>
              <HighlightOrDisplay result={orderResult} field="contactEmail" />
            </td>
          </tr>
          <tr>
            <PaddedCell>
              <LabelText>PAY METHOD</LabelText>
            </PaddedCell>
            <td>
              <ValueText>
                {orderResult.data.paymentMethod
                  ? toReadableString(orderResult.data.paymentMethod)
                  : '--'}
              </ValueText>
            </td>
          </tr>
          <tr>
            <PaddedCell>
              <LabelText>PRICE</LabelText>
            </PaddedCell>
            <td>
              <ValueText useDataFont>
                {orderResult.data.totalPrice
                  ? orderResult.data.totalPrice
                  : '--'}
              </ValueText>
            </td>
          </tr>
          <tr>
            <PaddedCell>
              <LabelText>ORDERED</LabelText>
            </PaddedCell>
            <td>
              <ValueText useDataFont>
                {orderResult.data.created
                  ? moment(orderResult.data.created)
                      .subtract(new Date().getTimezoneOffset(), 'minutes')
                      .format(dateAtTimeFormat)
                  : '--'}
              </ValueText>
            </td>
          </tr>
          <tr>
            <PaddedCell>
              <LabelText>SHIPS</LabelText>
            </PaddedCell>
            <td>
              <ValueText useDataFont>
                {orderResult.data.shipsOn
                  ? orderResult.data.shipsOn
                      .map((shipsOnDate: string) =>
                        moment(shipsOnDate)
                          .subtract(new Date().getTimezoneOffset(), 'minutes')
                          .format(dateAtTimeFormat),
                      )
                      .join(', ')
                  : '--'}
              </ValueText>
            </td>
          </tr>
        </tbody>
      </table>
    </Box>
  );
};

export default OrderMatchCell;
